// Internal
import type {
  BreakdownMappedV2,
  CurrencyMappedV2,
  NormalizedAddressForMerchantV2,
  NormalizedShippingOptionV2,
} from './types';
import type {
  AddressAreEqualOptions,
  Breakdown,
  Currency,
  EmptyObject,
  MappedAddressValues,
  ShippingOption,
} from '../types';

/**
 * TODO: Cleanup once shared shipping cb hooks are used
 * TODO: Remove this file along with the ordersV2Mappers
 *
 * folder once we migrate to shared hooks
 * Converts the cart amount breakdown from
 * gql schema to OrdersV2 amount_breakdown object
 * See https://developer.paypal.com/docs/api/orders/v2/#definition-amount_breakdown
 *
 * @param {*} breakdown cart amount breakdown from gql
 * @returns breakdown object in OrdersV2 Amount_breakdown schema
 */
const buildAmountBreakdownV2 = (
  breakdown: Breakdown = {},
): BreakdownMappedV2 => {
  return {
    discount: currencyMapperV2(breakdown.discount),
    handling: currencyMapperV2(breakdown.handlingFee),
    insurance: currencyMapperV2(breakdown.insurance),
    itemTotal: currencyMapperV2(breakdown.subtotal),
    shipping: currencyMapperV2(breakdown.shipping),
    shippingDiscount: currencyMapperV2(breakdown.shippingDiscount),
    taxTotal: currencyMapperV2(breakdown.tax),
  };
};

const currencyMapperV2 = (currencyObj: Currency = {}): CurrencyMappedV2 => ({
  currencyCode: currencyObj.currencyCode,
  value: currencyObj.currencyValue,
});

const normalizeShippingOptionV2 = (
  gqlShippingOption: ShippingOption,
): NormalizedShippingOptionV2 => {
  const { __typename, amount, ...shippingOption } = gqlShippingOption || {};
  return {
    ...shippingOption,
    amount: currencyMapperV2(amount),
  };
};

/**
 * TODO: Cleanup once shared shipping cb hooks are used
 *
 * Removes line1, line2 from address object if it exists.
 * We do not want to share these components with the merchant
 * at this point.
 *
 * @param {object} address object with line1, line2, city,
 * state, postalcode, country code
 */
const normalizeAddressForMerchantV2 = (
  address: MappedAddressValues | EmptyObject,
): NormalizedAddressForMerchantV2 => {
  const { city, state, country, postalCode } = address ?? {};

  return {
    city,
    countryCode: country,
    postalCode: postalCode,
    state,
  };
};

/**
 * TODO: Cleanup once shared shipping cb hooks are used
 *
 * A helper function to compare two address component objects.
 * Allows configuring how many address
 * components need to match for the two addresses be to be considered equals.
 *
 * @param {address object} address1 address components object, line1,
 * line2, city, state, postalCode, countryCode
 * @param {address object} address2 address components object, line1,
 * line2, city, state, postalCode, countryCode
 * @param {} options, options object to configure equality,
 * options.specificity determines how to specific should the two
 * addresses be to be considered equals,
 * options.caseSensivity whether or not each component to match case as well
 * @returns boolean, true if both addresses are equal according to configuration
 */
const addressAreEqualV2 = (
  address1: NormalizedAddressForMerchantV2 | EmptyObject,
  address2: NormalizedAddressForMerchantV2 | EmptyObject,
  options: AddressAreEqualOptions = {
    ignoreCase: true,
    modifiers: [],
    specificity: 4,
  },
): boolean => {
  const applyModifers = (
    str: string,
    modifiers: Array<(data: string) => string>,
  ): string => {
    let modifiedStr: string = str;

    modifiers.forEach(modifier => {
      modifiedStr = modifier(modifiedStr);
    });
    return modifiedStr;
  };

  const caseInsensivityModifer = (str: string): string => str.toLowerCase();

  // All address components to check against, subset by specificity
  const addrComponentsToCheck = new Set(
    ['line1', 'line2', 'city', 'state', 'postalCode', 'countryCode'].slice(
      options.specificity * -1,
    ),
  );

  options.ignoreCase && options.modifiers.push(caseInsensivityModifer);

  // Find at least one address component which is different
  return Array.from(addrComponentsToCheck).every((addrKey: string) => {
    // check key's value against address2's key
    const addr1CompVal = String(
      address1[addrKey as keyof NormalizedAddressForMerchantV2],
    );
    const addr2CompVal = String(
      address2[addrKey as keyof NormalizedAddressForMerchantV2],
    );

    return (
      applyModifers(addr1CompVal, options.modifiers) ===
      applyModifers(addr2CompVal, options.modifiers)
    );
  });
};

export {
  addressAreEqualV2,
  buildAmountBreakdownV2,
  currencyMapperV2,
  normalizeAddressForMerchantV2,
  normalizeShippingOptionV2,
};
